import { Fragment, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChangeOfAddressType, ShippingAddredKeys } from "../../../../constants";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { GlobalContext } from "../../../../Context/GlobalContext";
import AutoSuggestField from "../../../../Layout/Common/autoSuggestField";
import { isNumberOnly, isValidArray, scrollToTop } from "../../../../Layout/Common/common";
import { RequiredFieldErrorMessage } from "../../../../Layout/Common/alert/RequiredFieldErrorMessage";
import { DeliveryInstructionReuired } from "../../../../Layout/Common/alert/DeliveryInstructionReuired";
import { PAGES } from "../../../../routeConfig";
import { AddressBookSVG } from "../../../../assets/imageImport";
import { AddressBookList } from "./addressBook/addressBookList";
import { useGlobalFunction } from "../../../../Context/GlobalFunctionContext";
import { convertAddressBookFormat, convertAddressBookJson } from "../../checkoutFunction";
import { FormikErrorMessage } from "../../../../Layout/Common/fromikErrorMessage";
import { debounce } from "lodash";
import { Modal } from "bootstrap";
export const ChangeOfAddress = ({ onUpdate, onCancel }) => {
  const {
    newAddress,
    setNewAddress,
    isDropshipmentUser,
    addressType,
    setAddressType,
    shippingAddress,
    shippingAddressOriginal,
    setShippingAddress,
    setChangeAddressClicked,
    setSelectedAddressId,
    suburbList,
  } = useContext(GlobalContext);

  const { saveAddressBook } = useGlobalFunction();
  const [prevData, setPrevData] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [isBookOpen, setIsBookOpen] = useState(false);
  // > 0 when editing an existing address-book entry (drives "Update Address" label + which entry gets updated).
  const [editId, setEditId] = useState(0);
  const childRequiredRef = useRef();
  const childSignRef = useRef();
  const modalRef = useRef(null);
  const { changeOfAddress, dropShippment } = ChangeOfAddressType;

  useEffect(() => {
    !addressType && setAddressType(changeOfAddress);

    setPrevData(shippingAddress);
    if (shippingAddress) {
      let address = newAddress || {};
      setNewAddress({
        shipTo: address.shipTo ?? "",
        shipAddress1: address.shipAddress1 ?? "",
        shipAddress2: address.shipAddress2 ?? "",
        shipAddress3: address.shipAddress3 ?? "",
        shipCity: address.shipCity ?? "",
        shipState: address.shipState ?? "",
        shipZip: address.shipZip ?? "",
        shipCountry: address.shipCountry ?? "Australia",
        shipPhone: address.shipPhone ?? "",
        shipAttention: address.shipAttention ?? "",
        deliveryInst: address.deliveryInst ?? "",
        isSaveAddress: address.isSaveAddress ?? false,
        deliveryType: addressType && addressType == dropShippment ? dropShippment : changeOfAddress,
        isDropShipping: addressType && addressType == dropShippment ? true : false,
        signatureRequired: address.signatureRequired ?? true,
      });
    }
  }, [shippingAddress]);

  // Make sure the address-book modal and its backdrop never outlive this component.
  useEffect(() => {
    scrollToTop();
    return () => {
      if (modalRef.current) {
        const instance = Modal.getInstance(modalRef.current);
        if (instance) instance.dispose();
      }
      document.body.classList.remove("modal-open");
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
      document.querySelectorAll(".modal-backdrop").forEach((b) => b.remove());
    };
  }, []);

  const handleSubmit = (values) => {
    setNewAddress(values);
    onUpdate(values);
    // When editing an existing entry (editId > 0), always persist the changes.
    // Otherwise persist only if the user ticked "Save to Address book".
    if (editId || values.isSaveAddress == true) {
      handleAddressSave(values);
    }
  };

  const handleCancel = () => {
    setAddressType(null);
    setSelectedAddressId(null);
    let originalAddress = shippingAddressOriginal;
    setNewAddress(null);
    if (originalAddress) onUpdate(originalAddress);
    else onUpdate(prevData);
  };

  const checkRequiredField = (isValid, errors) => {
    if (!isValid) {
      childRequiredRef.current.showAlert();
    }
    if (errors["deliveryInst"]) childSignRef.current.showAlert();
  };

  const handleOnChangeField = (e, key, setFieldValue) => {
    setFieldValue(key, e.target.value);
    debouncedFunction(key, e.target.value);
  };

  const debouncedFunction = useCallback(
    debounce((key, value) => {
      setShippingAddress((prev) => ({
        ...prev,
        [key]: value,
      }));
    }, 1000),
    [],
  );
  const handleAddressSave = async (values) => {
    try {
      var data = convertAddressBookJson(values);
      // editId > 0 updates that existing entry; 0 creates a new one.
      saveAddressBook(data, editId);
    } catch (e) {
    }
  };
  const handleSuburbChange = (setFieldValue, suburb) => {
    if (suburb) {
      setFieldValue("shipState", suburb.state);
      setFieldValue("shipCity", suburb.suburb);
      setFieldValue("shipZip", suburb.postCode);

      setShippingAddress((prev) => ({
        ...prev,
        shipState: suburb.state,
        shipCity: suburb.suburb,
        shipZip: suburb.postCode,
      }));
    }
  };

  const openAddressBook = () => {
    setIsBookOpen(true);
    if (modalRef.current) Modal.getOrCreateInstance(modalRef.current).show();
  };

  const closeAddressBook = () => {
    if (modalRef.current) {
      const instance = Modal.getInstance(modalRef.current);
      if (instance) instance.hide();
    }
    setIsBookOpen(false);
  };

  // Pick an address from the book and apply it to the order, then return to main.
  // Persist it into newAddress so re-opening "Change" shows it prefilled (not a blank form).
  const handleSelectFromBook = (item) => {
    const address = convertAddressBookFormat(item);
    setSelectedAddressId(item.id);
    setAddressType(item.isDropShipping == true ? dropShippment : changeOfAddress);
    setEditId(0);
    setNewAddress({
      ...address,
      deliveryType: item.isDropShipping == true ? dropShippment : changeOfAddress,
      isSaveAddress: false,
    });
    closeAddressBook();
    onUpdate(address);
    setChangeAddressClicked(false);
  };

  // Edit an address from the book: prefill this form, stay on it until the user saves.
  // We're updating an existing entry, so default the "Update Address" checkbox to checked.
  const handleEditFromBook = (item) => {
    const address = convertAddressBookFormat(item);
    setAddressType(address.isDropShipping == true ? dropShippment : changeOfAddress);
    setEditId(item.id ?? 0);
    setNewAddress({
      ...address,
      deliveryType: address.isDropShipping == true ? dropShippment : changeOfAddress,
      isSaveAddress: true,
    });
    setFormKey((key) => key + 1);
    closeAddressBook();
  };

  // Deselect (revert to the default account address) and return to main.
  const handleResetFromBook = () => {
    setSelectedAddressId(null);
    setAddressType(null);
    closeAddressBook();
    onUpdate(shippingAddressOriginal || prevData);
    setChangeAddressClicked(false);
  };

  // Clear every field back to a blank "new address" form (exits edit mode).
  const handleClearForm = () => {
    setEditId(0);
    setAddressType(changeOfAddress);
    setNewAddress({
      shipTo: "",
      shipAddress1: "",
      shipAddress2: "",
      shipAddress3: "",
      shipCity: "",
      shipState: "",
      shipZip: "",
      shipCountry: "Australia",
      shipPhone: "",
      shipAttention: "",
      deliveryInst: "",
      isSaveAddress: false,
      deliveryType: changeOfAddress,
      isDropShipping: false,
      signatureRequired: true,
    });
    setFormKey((key) => key + 1);
  };

  const validationSchema = Yup.object().shape({
    shipTo: Yup.string().required("Recipient name is required"),
    shipAddress1: Yup.string().required("Address Line 1 is required"),
    // shipAddress2: Yup.string().required("Address Line 2 is required"),
    shipCity: Yup.string().required("City is required"),
    shipState: Yup.string().required("State is required"),
    shipAttention: Yup.string().required("Attention Name is required"),
    shipZip: Yup.string()
      .required("ZIP code is required")
      .matches(/^\d{3,6}$/, "ZIP code is incorrect"),
    shipCountry: Yup.string().required("Country is required"),
    shipPhone: Yup.string().required("Phone number is required"),
    signatureRequired: Yup.boolean(),
    deliveryInst: Yup.string().test("deliveryInst-required", "Delivery instruction is required", function (value) {
      const { signatureRequired } = this.parent;
      if (!signatureRequired && !value) {
        return false;
      }
      return true;
    }),
  });

  return (
    <Fragment>
      {newAddress && (
        <div className="fz-container">
          <div className="row col-lg-12 checkout-lft-title">
            <div className="wesproduct-title cart-title">
              <h1>Shipping Address Update</h1>
              <button type="button" className="a-tag-button return-link" onClick={onCancel}>
                <i className="fa-solid fa-chevron-left" /> Go Back
              </button>
            </div>
          </div>
          <div className="col-lg-12 checkout-lft-title">
            <div className="delivery-method mt-3">
              <div className="row">
                <div className="col-md-12">
                  <div className="shippingAddress-update">
                    <p>
                      One-time address change only. Your default account shipping address will not be updated. To update
                      your default account shipping address. <span>Go to My Account /</span>
                      <Link to={PAGES.MYACCOUNTDETAIL}>View Profile</Link>
                    </p>
                  </div>
                </div>
              </div>
              <Formik
                key={formKey}
                initialValues={newAddress}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({ setFieldValue, errors, isValid, touched, values, ...formik }) => (
                  <Form>
                    <div className="row">
                      <div className="col-md-12">
                        <div className="shippAdd-title shippingAdd-title">
                          <h3 className="mb-0">Enter New Delivery Address</h3>
                          <section>
                            {/* <button
                              type="button"
                              className="  text-decoration-underline"
                              onClick={handleClearForm}
                              aria-label="Close"
                            >
                              <i class="fa-solid fa-circle-xmark"></i> Clear
                            </button> */}
                            <button
                              type="button"
                              className="a-tag-button text-decoration-underline"
                              onClick={openAddressBook}
                            >
                              <AddressBookSVG />
                              My Address Book
                            </button>
                          </section>
                        </div>
                      </div>
                      <div className="col-md-12">
                        <div className="shippingForms-inner">
                          <div className="shippChange-address">
                            <div className="row">
                              <div className="col-md-6 col-lg-12">
                                <div className="floating-input mb-3">
                                  <Field
                                    id="shipTo"
                                    name="shipTo"
                                    className={`form-control form-input ${
                                      touched.shipTo && errors.shipTo && "error-message"
                                    }`}
                                    type="text"
                                    placeholder=""
                                    required={true}
                                  />
                                  <label htmlFor="shipTo">
                                    Company/Receiver Name
                                    <span className="gl-form-asterisk">*</span>
                                  </label>
                                  <ErrorMessage name="shipTo">
                                    {(msg) => <FormikErrorMessage message={msg} />}
                                  </ErrorMessage>
                                </div>
                              </div>

                              <div className="col-md-6 col-lg-6">
                                <div className="floating-input mb-3">
                                  <Field
                                    id="shipAddress1"
                                    name="shipAddress1"
                                    className={`form-control form-input ${
                                      touched.shipAddress1 && errors.shipAddress1 && "error-message"
                                    }`}
                                    type="text"
                                    placeholder=""
                                    required={true}
                                    onChange={(e) => handleOnChangeField(e, "shipAddress1", setFieldValue)}
                                  />
                                  <label htmlFor="shipAddress1">
                                    Address1
                                    <span className="gl-form-asterisk">*</span>
                                  </label>
                                  <ErrorMessage name="shipAddress1">
                                    {(msg) => <FormikErrorMessage message={msg} />}
                                  </ErrorMessage>
                                </div>
                              </div>

                              <div className="col-md-6 col-lg-6">
                                <div className="floating-input mb-3">
                                  <Field
                                    id="shipAddress2"
                                    name="shipAddress2"
                                    className={`form-control form-input`}
                                    type="text"
                                    placeholder=""
                                    onChange={(e) => handleOnChangeField(e, "shipAddress2", setFieldValue)}
                                  />
                                  <label htmlFor="shipAddress2">Address2</label>
                                  <ErrorMessage
                                    name="shipAddress2"
                                    component="div"
                                    className="text-danger form-validation-message"
                                  />
                                </div>
                              </div>

                              <div className="col-md-6 col-lg-6">
                                <div className="floating-input mb-3">
                                  <AutoSuggestField
                                    formik={formik}
                                    setFieldValue={setFieldValue}
                                    formValue={values}
                                    suggestions={suburbList}
                                    name="shipCity"
                                    label="Enter Suburb details"
                                    callBackFunction={handleSuburbChange}
                                    errors={errors.shipCity}
                                    touched={touched.shipCity}
                                    required={true}
                                    autoComplete="off"
                                  />
                                  <ErrorMessage name="shipCity">
                                    {(msg) => <FormikErrorMessage message={msg} />}
                                  </ErrorMessage>
                                </div>
                              </div>

                              <div className="col-md-6 col-lg-6">
                                <div className="floating-input  mb-3">
                                  <Field
                                    type="text"
                                    id="shipState"
                                    name="shipState"
                                    disabled={true}
                                    className={`form-control form-input ${
                                      touched.shipState && errors.shipState && "error-message"
                                    }`}
                                    onChange={(e) => handleOnChangeField(e, "shipState", setFieldValue)}
                                    placeholder=""
                                  ></Field>
                                  <label htmlFor="shipState">
                                    State
                                    <span className="gl-form-asterisk">*</span>
                                  </label>
                                  <ErrorMessage name="shipState">
                                    {(msg) => <FormikErrorMessage message={msg} />}
                                  </ErrorMessage>
                                </div>
                              </div>
                              <div className="col-md-6 col-lg-6">
                                <div className="floating-input mb-3">
                                  <Field
                                    id="shipZip"
                                    name="shipZip"
                                    disabled={true}
                                    className={`form-control form-input ${
                                      touched.shipZip && errors.shipZip && `error-message`
                                    }`}
                                    type="text"
                                    placeholder=""
                                  />
                                  <label htmlFor="shipZip">
                                    ZIP Code <span className="gl-form-asterisk">*</span>
                                  </label>
                                  <ErrorMessage name="shipZip">
                                    {(msg) => <FormikErrorMessage message={msg} />}
                                  </ErrorMessage>
                                </div>
                              </div>
                              <div className="col-md-6 col-lg-6">
                                <div className="floating-input mb-3">
                                  <Field
                                    id="shipCountry"
                                    disabled={true}
                                    name="shipCountry"
                                    className={`form-control form-input ${
                                      touched.shipCountry && errors.shipCountry && "error-message"
                                    }`}
                                    type="text"
                                    placeholder=""
                                    defaultValue="Australia"
                                  />
                                  <label htmlFor="shipCountry">
                                    Country <span className="gl-form-asterisk">*</span>
                                  </label>
                                  <ErrorMessage name="shipCountry">
                                    {(msg) => <FormikErrorMessage message={msg} />}
                                  </ErrorMessage>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="shippingForms-inner">
                          <div className="receiver-details">
                            <h3>Receiver Details</h3>
                            <div className="row">
                              <div className="col-md-6 col-lg-6">
                                <div className="floating-input mb-3">
                                  <Field
                                    id="shipPhone"
                                    name="shipPhone"
                                    className={`form-control form-input ${
                                      touched.shipPhone && errors.shipPhone && "error-message"
                                    }`}
                                    type="text"
                                    placeholder=""
                                    onChange={(e) => {
                                      //  values["shipPhone"] = e.target.value;
                                      setFieldValue("shipPhone", isNumberOnly(e.target.value));
                                    }}
                                    required={true}
                                  />
                                  <label htmlFor="shipPhone">
                                    Phone Number
                                    <span className="gl-form-asterisk">*</span>
                                  </label>
                                  <ErrorMessage name="shipPhone">
                                    {(msg) => <FormikErrorMessage message={msg} />}
                                  </ErrorMessage>
                                </div>
                              </div>
                              <div className="col-md-6 col-lg-6">
                                <div className="floating-input mb-3">
                                  <Field
                                    id="shipAttention"
                                    name="shipAttention"
                                    className={`form-control form-input ${
                                      touched.shipAttention && errors.shipAttention && "error-message"
                                    }`}
                                    type="text"
                                    placeholder=""
                                    required={true}
                                  />
                                  <label htmlFor="shipAttention">
                                    Attention of Name
                                    <span className="gl-form-asterisk">*</span>
                                  </label>
                                  <ErrorMessage name="shipAttention">
                                    {(msg) => <FormikErrorMessage message={msg} />}
                                  </ErrorMessage>
                                </div>
                              </div>

                              <div className="col-md-6 col-lg-12">
                                <div className="floating-input mb-3">
                                  <Field
                                    id="deliveryInst"
                                    name="deliveryInst"
                                    className={`form-control form-input`}
                                    type="text"
                                    placeholder=""
                                  />
                                  <label htmlFor="deliveryInst">Delivery Instruction</label>

                                  <ErrorMessage name="deliveryInst">
                                    {(msg) => <FormikErrorMessage message={msg} />}
                                  </ErrorMessage>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="shippingForms-inner">
                          <div className="signature-required">
                            <h3>Delivery Type</h3>
                            <div className="row row-equal">
                              <div className="col-lg-6">
                                <div className="form-check custom-check signature-req mb-3 mb-lg-0">
                                  <div className="signature-req-input">
                                    <input
                                      className="form-check-input"
                                      type="radio"
                                      name="deliveryType"
                                      id="deliveryType1"
                                      value={changeOfAddress}
                                      checked={addressType == changeOfAddress}
                                      onChange={() => {
                                        setAddressType(changeOfAddress);
                                        setFieldValue("deliveryType", changeOfAddress);
                                      }}
                                    />
                                  </div>
                                  <label className="form-check-label" htmlFor="deliveryType1">
                                    Change of Address
                                    <span>Invoice include with order</span>
                                  </label>
                                </div>
                              </div>
                              {isDropshipmentUser && isDropshipmentUser == true && (
                                <div className="col-lg-6">
                                  <div className="form-check custom-check signature-req">
                                    <div className="signature-req-input">
                                      <input
                                        className={`form-check-input`}
                                        type="radio"
                                        name="deliveryType"
                                        id="deliveryType2"
                                        value={dropShippment}
                                        checked={addressType == dropShippment}
                                        onChange={() => {
                                          setAddressType(dropShippment);
                                          setFieldValue("deliveryType", dropShippment);
                                        }}
                                      />
                                    </div>
                                    <label className="form-check-label" htmlFor="deliveryType2">
                                      Drop Shipment Order <span>No invoice sent with order</span>
                                    </label>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="shippingForms-inner">
                          <div className="signature-required">
                            <h3>Signature Required</h3>
                            <div className="row row-equal">
                              <div className="col-lg-6">
                                <div className="form-check custom-check signature-req mb-3 mb-md-0">
                                  <div className="signature-req-input">
                                    <Field
                                      className="form-check-input"
                                      type="radio"
                                      name="signatureRequired"
                                      id="signatureIsRequired"
                                      value={true}
                                      checked={values.signatureRequired == true}
                                      onChange={() => setFieldValue("signatureRequired", true)}
                                    />
                                  </div>
                                  <label className="form-check-label" htmlFor="signatureIsRequired">
                                    Yes, Required
                                    <span>Someone will be required to sign for parcel.</span>
                                  </label>
                                </div>
                              </div>
                              <div className="col-lg-6">
                                <div className="form-check custom-check signature-req">
                                  <div className="signature-req-input">
                                    <Field
                                      className="form-check-input"
                                      type="radio"
                                      name="signatureRequired"
                                      id="signatureNotRequired"
                                      value={false}
                                      checked={values.signatureRequired == false}
                                      onChange={() => setFieldValue("signatureRequired", false)}
                                    />
                                  </div>
                                  <label className="form-check-label" htmlFor="signatureNotRequired">
                                    No, Not Required. Authority to leave
                                    <span>Add 'Delivery Instructions' in the field above.</span>
                                  </label>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="saveAddress-book">
                          <div className="form-check custom-check ">
                            <div className="saveAdd-bk">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                name="isSaveAddress"
                                id="saveAdd-bookForm"
                                checked={editId ? true : values.isSaveAddress == true}
                                disabled={editId ? true : false}
                                onChange={(e) => {
                                  setFieldValue("isSaveAddress", e.target.checked);
                                }}
                              />
                            </div>
                            <label className="form-check-label fs-14" htmlFor="saveAdd-bookForm">
                              {editId ? "Update Address" : "Save to Address book"}
                            </label>
                          </div>
                        </div>
                        <div className="saveAddress-btn mt-0">
                          <RequiredFieldErrorMessage ref={childRequiredRef} />
                          <DeliveryInstructionReuired ref={childSignRef} />
                        </div>

                        <section className=" mt-4" style={{ display: "flex", justifyContent: "space-between" }}>
                          <div className="saveAddress-btn mt-0">
                            <div className="button-effect">
                              <button
                                type="submit"
                                className="btn btn-products btn-addcart"
                                onClick={() => checkRequiredField(isValid, errors)}
                              >
                                Save Address
                              </button>
                            </div>
                            <div className="product-button">
                              <button type="button" className="btn btn-products btn-unavailable" onClick={handleCancel}>
                                Set Default
                              </button>
                            </div>
                          </div>
                          <button type="button" className="a-tag-button return-link" onClick={onCancel}>
                            <i className="fa-solid fa-chevron-left" /> Go Back
                          </button>
                        </section>
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      )}
      <div
        className="modal fade"
        id="checkoutAddressBookModal"
        ref={modalRef}
        data-bs-backdrop="static"
        data-bs-keyboard="false"
        tabIndex={-1}
        aria-labelledby="checkoutAddressBookModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-animate-center modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <div className="wesproduct-title">
                <h1 className="mb-0" id="checkoutAddressBookModalLabel">
                  Shipping Address Update
                </h1>
              </div>
              <button type="button" className="btn-close" onClick={closeAddressBook} aria-label="Close" />
            </div>
            <div className="modal-body">
              {isBookOpen && (
                <div className="fz-container">
                  <div className="row">
                    <AddressBookList
                      onEdit={handleEditFromBook}
                      onSelect={handleSelectFromBook}
                      onReset={handleResetFromBook}
                      onCancel={closeAddressBook}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};
